import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useManagerTheme } from '../context/ManagerThemeContext';
import ManagerSidebar from '../components/ManagerSidebar';
import ManagerHeader from '../components/ManagerHeader';
import Footer from '../components/Footer';
import AutocompleteInput from '../components/AutocompleteInput';
import DarkSelect from '../components/DarkSelect';
import api from '../services/api';
import LogoutModal from '../components/LogoutModal';
import { scheduleLoadingEnd } from '../utils/loadingDelay';

interface EscrowTransaction {
  id: string;
  merchant: string;
  createdBy: string;
  type: 'Debit' | 'Credit';
  transactionId: string;
  dateTime: string;
  amount: number;
  currentWalletBalance: number;
  isHighlighted?: boolean;
  autoReleaseEnabled?: boolean;
  autoReleaseType?: string;
  autoReleaseDate?: string;
}

interface EscrowFilters {
  fromDate: string;
  toDate: string;
  paymentType: string;
  transactionId: string;
}

interface Merchant {
  id: number;
  name: string;
  email: string;
  ownerName?: string;
  businessName?: string;
}

interface AutocompleteOption {
  id: string | number;
  label: string;
  subtitle?: string;
  type: 'user' | 'merchant';
}

const ManagerEscrow: React.FC = () => {
  const { user, logout } = useAuth();
  const { sidebarColor, contentColor, sectionColor, tableColor } = useManagerTheme();
  const [transactions, setTransactions] = useState<EscrowTransaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('escrow');
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(84);
  const loadingStartRef = useRef(0);
  const [filters, setFilters] = useState<EscrowFilters>({
    fromDate: '',
    toDate: '',
    paymentType: '',
    transactionId: ''
  });

  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showActionModal, setShowActionModal] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState<EscrowTransaction | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Form data
  const [createFormData, setCreateFormData] = useState({
    merchantId: '',
    transactionType: 'debit',
    amount: '',
    description: '',
    contactInfo: '',
    paymentMethod: '',
    commissionAmount: '0',
    processingFee: '0',
    autoReleaseType: 'manual', // 'manual', 'time', 'api', 'customer'
    autoReleaseValue: '', // days for time, URL for api, email for customer
    autoReleaseEnabled: false
  });

  const [actionFormData, setActionFormData] = useState({
    action: 'release', // 'release' or 'refund'
    processedBy: ''
  });

  // Merchant selection states
  const [merchants, setMerchants] = useState<Merchant[]>([]);
  const [filteredMerchants, setFilteredMerchants] = useState<Merchant[]>([]);
  const [merchantSearchTerm, setMerchantSearchTerm] = useState('');
  const [showMerchantDropdown, setShowMerchantDropdown] = useState(false);
  const [selectedMerchant, setSelectedMerchant] = useState<Merchant | null>(null);
  const [merchantSearchLoading, setMerchantSearchLoading] = useState(false);

  // Autocomplete states
  const [autocompleteOptions, setAutocompleteOptions] = useState<AutocompleteOption[]>([]);
  const [loadingAutocomplete, setLoadingAutocomplete] = useState(false);

  useEffect(() => {
    fetchEscrowData();
    fetchMerchants();
    fetchAutocompleteOptions();
  }, []);

  // Refetch data when page changes
  useEffect(() => {
    if (currentPage > 1) {
      fetchEscrowData();
    }
  }, [currentPage]);

  // Debounced merchant search
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (merchantSearchTerm.trim()) {
        searchMerchants(merchantSearchTerm.trim());
      } else {
        setFilteredMerchants([]);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [merchantSearchTerm]);

  const fetchEscrowData = async () => {
    try {
      loadingStartRef.current = Date.now();
      setLoading(true);
      
      // Fetch real escrow data from API
      const params: any = {
        page: currentPage,
        limit: 50
      };

      // Map frontend filter names to backend parameter names
      if (filters.fromDate) params.date_from = filters.fromDate;
      if (filters.toDate) params.date_to = filters.toDate;
      if (filters.paymentType) params.transaction_type = filters.paymentType;
      if (filters.transactionId) params.transaction_id = filters.transactionId;

      console.log('🔍 Escrow API Request Params:', params);
      console.log('🔍 Current Filters:', filters);

      const response = await api.get('/escrow', { params });

      if (response.data.success) {
            const apiTransactions = response.data.data.transactions.map((transaction: any) => ({
              id: transaction.id.toString(),
              merchant: transaction.contact_info || transaction.merchant_name || 'Unknown Merchant',
              createdBy: transaction.created_by,
              type: transaction.transaction_type === 'credit' ? 'Credit' : 'Debit',
              transactionId: transaction.transaction_id,
              dateTime: transaction.created_at,
              amount: parseFloat(transaction.amount),
              currentWalletBalance: parseFloat(transaction.current_wallet_balance),
              isHighlighted: transaction.status === 'pending',
              autoReleaseEnabled: transaction.auto_release_enabled || false,
              autoReleaseType: transaction.auto_release_type || 'manual',
              autoReleaseDate: transaction.auto_release_date
            }));
        
        setTransactions(apiTransactions);
        setTotalPages(response.data.data.pagination.totalPages);
      } else {
        throw new Error(response.data.message || 'Failed to fetch escrow data');
      }
      
    } catch (error: any) {
      console.error('Error fetching escrow data:', error);
      
      // Fallback to mock data if API fails
      const mockTransactions: EscrowTransaction[] = [
        {
          id: '1',
          merchant: 'Lotus14',
          createdBy: 'Stackspire-Escrow',
          type: 'Debit',
          transactionId: 'ESCROCREDIT81MERCHANT1760158875',
          dateTime: '2025-10-11 10:31:15',
          amount: 200000,
          currentWalletBalance: 283128,
          isHighlighted: true
        },
        {
          id: '2',
          merchant: 'Lotus5',
          createdBy: 'Stackspire-Escrow',
          type: 'Debit',
          transactionId: 'ESCROCREDIT81MERCHANT1760158875',
          dateTime: '2025-10-11 10:31:15',
          amount: 200000,
          currentWalletBalance: 283128,
          isHighlighted: true
        },
        {
          id: '3',
          merchant: 'LOTUS7',
          createdBy: 'Stackspire-Escrow',
          type: 'Debit',
          transactionId: 'ESCROCREDIT81MERCHANT1760158875',
          dateTime: '2025-10-11 10:31:15',
          amount: 200000,
          currentWalletBalance: 283128,
          isHighlighted: true
        },
        {
          id: '4',
          merchant: 'L8VICKY',
          createdBy: 'Stackspire-Escrow',
          type: 'Debit',
          transactionId: 'ESCROCREDIT81MERCHANT1760158875',
          dateTime: '2025-10-11 10:31:15',
          amount: 200000,
          currentWalletBalance: 283128,
          isHighlighted: true
        },
        {
          id: '5',
          merchant: 'LOTUSEXCH33',
          createdBy: 'Stackspire-Escrow',
          type: 'Debit',
          transactionId: 'ESCROCREDIT81MERCHANT1760158875',
          dateTime: '2025-10-11 10:31:15',
          amount: 200000,
          currentWalletBalance: 283128,
          isHighlighted: true
        },
        {
          id: '6',
          merchant: 'Manual4',
          createdBy: 'Stackspire-Escrow',
          type: 'Debit',
          transactionId: 'ESCROCREDIT81MERCHANT1760158875',
          dateTime: '2025-10-11 10:31:15',
          amount: 200000,
          currentWalletBalance: 283128,
          isHighlighted: true
        },
        {
          id: '7',
          merchant: 'Manual3',
          createdBy: 'Stackspire-Escrow',
          type: 'Debit',
          transactionId: 'ESCROCREDIT81MERCHANT1760158875',
          dateTime: '2025-10-11 10:31:15',
          amount: 200000,
          currentWalletBalance: 283128,
          isHighlighted: true
        },
        {
          id: '8',
          merchant: 'Stackspire-Escrow',
          createdBy: 'Stackspire-Escrow',
          type: 'Credit',
          transactionId: 'ESCROCREDIT81MERCHANT1760158875',
          dateTime: '2025-10-11 10:31:15',
          amount: 200000,
          currentWalletBalance: 283128,
          isHighlighted: false
        },
        {
          id: '9',
          merchant: 'Stackspire-Escrow',
          createdBy: 'Stackspire-Escrow',
          type: 'Credit',
          transactionId: 'ESCROCREDIT81MERCHANT1760158875',
          dateTime: '2025-10-11 10:31:15',
          amount: 200000,
          currentWalletBalance: 283128,
          isHighlighted: false
        },
        {
          id: '10',
          merchant: 'Stackspire-Escrow',
          createdBy: 'Stackspire-Escrow',
          type: 'Credit',
          transactionId: 'ESCROCREDIT81MERCHANT1760158875',
          dateTime: '2025-10-11 10:31:15',
          amount: 200000,
          currentWalletBalance: 283128,
          isHighlighted: false
        }
      ];

      // In real app, fetch from API:
      // const response = await api.get('/manager/escrow-transactions');
      // setTransactions(response.data.data);

      setTransactions(mockTransactions);
    } finally {
      scheduleLoadingEnd(loadingStartRef.current, setLoading);
    }
  };

  const fetchMerchants = async () => {
    try {
      const response = await api.get('/merchants');
      console.log('Merchants API response:', response.data);
      
      if (response.data.success) {
        const merchantList = response.data.data.map((merchant: any) => {
          console.log('Processing merchant:', merchant);
          
          // Use the correct field names from the API response
          const ownerName = merchant.ownerName || '';
          const businessName = merchant.businessName || '';
          let displayName = '';

          // Create display name in format: "Name - Business Name - Email"
          const cleanOwnerName = ownerName && ownerName.trim() && ownerName !== 'null' ? ownerName.trim() : '';
          const cleanBusinessName = businessName && businessName.trim() && businessName !== 'null' ? businessName.trim() : '';
          const cleanEmail = merchant.email || '';
          
          // Build display name with available information
          const nameParts = [];
          
          if (cleanOwnerName) {
            nameParts.push(cleanOwnerName);
          }
          
          if (cleanBusinessName && cleanBusinessName !== cleanOwnerName) {
            nameParts.push(cleanBusinessName);
          }
          
          if (cleanEmail) {
            nameParts.push(cleanEmail);
          }
          
          displayName = nameParts.length > 0 ? nameParts.join(' - ') : `Merchant ${merchant.id}`;

          console.log(`Merchant ${merchant.id}: ownerName="${ownerName}", businessName="${businessName}", displayName="${displayName}"`);

          return {
            id: merchant.id,
            name: displayName || 'Unknown Merchant',
            email: merchant.email,
            ownerName: ownerName,
            businessName: businessName
          };
        });
        
        console.log('Processed merchant list:', merchantList);
        setMerchants(merchantList);
      }
    } catch (error) {
      console.error('Error fetching merchants:', error);
      // Fallback to mock merchants based on actual database data
      setMerchants([
        { id: 8, name: 'Merchant - merchant@yuvanapi.com', email: 'merchant@yuvanapi.com' },
        { id: 9, name: 'Test Merchant - Test Business - testmerchant@yuvanapi.com', email: 'testmerchant@yuvanapi.com' },
        { id: 10, name: 'Algo Basket - Algobasket - algobasket@gmail.com', email: 'algobasket@gmail.com' },
        { id: 11, name: 'Deepak Kumar - Test Business 2 - testmerchant2@yuvanapi.com', email: 'testmerchant2@yuvanapi.com' },
        { id: 12, name: 'Roki Miller - Algobasket - cryptotradestelugu@yuvanapi.com', email: 'cryptotradestelugu@yuvanapi.com' },
        { id: 13, name: 'Crypto Sap - Algobasket - cryptotap3@yuvanapi.com', email: 'cryptotap3@yuvanapi.com' },
        { id: 14, name: 'Crypto Tap - Algobasket - cryptotap@yuvanapi.com', email: 'cryptotap@yuvanapi.com' }
      ]);
    }
  };

  const searchMerchants = async (searchTerm: string) => {
    if (!searchTerm || searchTerm.length < 2) {
      setFilteredMerchants([]);
      return;
    }

    setMerchantSearchLoading(true);
    try {
      // First, try to search from the already loaded merchants
      const localResults = merchants.filter(merchant =>
        merchant.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        merchant.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        merchant.id.toString().includes(searchTerm)
      );

      // If we have enough local results, use them
      if (localResults.length >= 5) {
        setFilteredMerchants(localResults.slice(0, 10));
      } else {
        // Otherwise, make an API call for more comprehensive search
        const response = await api.get('/merchants', {
          params: {
            search: searchTerm,
            limit: 20
          }
        });

        if (response.data.success) {
          const searchResults = response.data.data.map((merchant: any) => {
            const ownerName = merchant.ownerName || '';
            const businessName = merchant.businessName || '';
            let displayName = '';

            // Create display name in format: "Name - Business Name - Email"
            const cleanOwnerName = ownerName && ownerName.trim() && ownerName !== 'null' ? ownerName.trim() : '';
            const cleanBusinessName = businessName && businessName.trim() && businessName !== 'null' ? businessName.trim() : '';
            const cleanEmail = merchant.email || '';
            
            // Build display name with available information
            const nameParts = [];
            
            if (cleanOwnerName) {
              nameParts.push(cleanOwnerName);
            }
            
            if (cleanBusinessName && cleanBusinessName !== cleanOwnerName) {
              nameParts.push(cleanBusinessName);
            }
            
            if (cleanEmail) {
              nameParts.push(cleanEmail);
            }
            
            displayName = nameParts.length > 0 ? nameParts.join(' - ') : `Merchant ${merchant.id}`;

            return {
              id: merchant.id,
              name: displayName || 'Unknown Merchant',
              email: merchant.email,
              ownerName: ownerName,
              businessName: businessName
            };
          });

          setFilteredMerchants(searchResults);
        } else {
          // Fallback to local search if API fails
          setFilteredMerchants(localResults);
        }
      }
    } catch (error) {
      console.error('Error searching merchants:', error);
      // Fallback to local search
      const localResults = merchants.filter(merchant =>
        merchant.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        merchant.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        merchant.id.toString().includes(searchTerm)
      );
      setFilteredMerchants(localResults);
    } finally {
      setMerchantSearchLoading(false);
    }
  };

  const fetchAutocompleteOptions = async () => {
    try {
      setLoadingAutocomplete(true);
      const response = await api.get('/dashboard/manager/users-merchants');
      if (response.data.success) {
        setAutocompleteOptions(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching autocomplete options:', error);
      // Fallback to empty array
      setAutocompleteOptions([]);
    } finally {
      setLoadingAutocomplete(false);
    }
  };

  // Handle autocomplete option selection
  const handleAutocompleteSelect = (option: AutocompleteOption) => {
    setActionFormData(prev => ({ ...prev, processedBy: option.label }));
  };

  // Handle merchant selection
  const handleMerchantSelect = (merchant: Merchant) => {
    setSelectedMerchant(merchant);
    setCreateFormData(prev => ({ ...prev, merchantId: merchant.id.toString() }));
    setMerchantSearchTerm(merchant.name); // merchant.name already includes email
    setShowMerchantDropdown(false);
  };

  const handleMerchantSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setMerchantSearchTerm(value);
    setShowMerchantDropdown(true);
    
    if (!value) {
      setSelectedMerchant(null);
      setCreateFormData(prev => ({ ...prev, merchantId: '' }));
      setFilteredMerchants([]);
    }
  };

  // Handle create escrow transaction
  const handleCreateEscrow = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!createFormData.merchantId || !createFormData.amount) {
      alert('Please fill in all required fields');
      return;
    }

    setSubmitting(true);

    try {
        const response = await api.post('/escrow/create', {
          merchant_id: parseInt(createFormData.merchantId),
          transaction_type: createFormData.transactionType,
          amount: parseFloat(createFormData.amount),
          description: createFormData.description,
          contact_info: createFormData.contactInfo,
          payment_method: createFormData.paymentMethod,
          commission_amount: parseFloat(createFormData.commissionAmount),
          processing_fee: parseFloat(createFormData.processingFee),
          auto_release: {
            enabled: createFormData.autoReleaseEnabled,
            type: createFormData.autoReleaseEnabled ? createFormData.autoReleaseType : 'manual',
            value: createFormData.autoReleaseEnabled ? createFormData.autoReleaseValue : null
          }
        });

      if (response.data.success) {
        setShowCreateModal(false);
        setCreateFormData({
          merchantId: '',
          transactionType: 'debit',
          amount: '',
          description: '',
          contactInfo: '',
          paymentMethod: '',
          commissionAmount: '0',
          processingFee: '0',
          autoReleaseType: 'manual',
          autoReleaseValue: '',
          autoReleaseEnabled: false
        });
        await fetchEscrowData();
        alert('Escrow transaction created successfully!');
      } else {
        throw new Error(response.data.message || 'Failed to create escrow transaction');
      }

    } catch (error: any) {
      console.error('Error creating escrow transaction:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Error creating escrow transaction. Please try again.';
      alert(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  // Handle escrow action (release/refund)
  const handleEscrowAction = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!selectedTransaction || !actionFormData.processedBy) {
      alert('Please fill in all required fields');
      return;
    }

    setSubmitting(true);

    try {
      const response = await api.post(`/escrow/${selectedTransaction.id}/release`, {
        action: actionFormData.action,
        processed_by: actionFormData.processedBy
      });

      if (response.data.success) {
        setShowActionModal(false);
        setSelectedTransaction(null);
        setActionFormData({
          action: 'release',
          processedBy: ''
        });
        await fetchEscrowData();
        alert(`Escrow transaction ${actionFormData.action}d successfully!`);
      } else {
        throw new Error(response.data.message || 'Failed to process escrow action');
      }

    } catch (error: any) {
      console.error('Error processing escrow action:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Error processing escrow action. Please try again.';
      alert(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  // Handle action button click
  const handleActionClick = (transaction: EscrowTransaction) => {
    setSelectedTransaction(transaction);
    setShowActionModal(true);
  };

  // Close modals
  const handleCloseCreateModal = () => {
    setShowCreateModal(false);
    setCreateFormData({
      merchantId: '',
      transactionType: 'debit',
      amount: '',
      description: '',
      contactInfo: '',
      paymentMethod: '',
      commissionAmount: '0',
      processingFee: '0',
      autoReleaseType: 'manual',
      autoReleaseValue: '',
      autoReleaseEnabled: false
    });
    // Clear merchant selection
    setSelectedMerchant(null);
    setMerchantSearchTerm('');
    setShowMerchantDropdown(false);
    setFilteredMerchants([]);
  };

  const handleCloseActionModal = () => {
    setShowActionModal(false);
    setSelectedTransaction(null);
    setActionFormData({
      action: 'release',
      processedBy: ''
    });
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(value);
  };

  const managerSidebarItems = [
    { id: 'home', label: 'Home', icon: '🏠' },
    { id: 'account', label: 'My Account', icon: '👤' },
    { id: 'payouts', label: 'Payouts', icon: '💰' },
    { id: 'reports', label: 'Reports', icon: '📊' },
    { id: 'merchants', label: 'Merchants', icon: '🏪' },
    { id: 'escrow', label: 'Escrow Transactions', icon: '🔒' },
    { id: 'credit', label: 'Credit History', icon: '💳' }
  ];

  const handleLogout = async () => {
    try {
      await logout();
      setShowLogoutModal(false);
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const handleLogoutClick = () => {
    setShowLogoutModal(true);
  };

  const handleCreditMoney = () => {
    // Navigate to credit money page or open modal
    console.log('Credit money clicked');
  };

  const handleMyProfile = () => {
    // Navigate to profile page
    console.log('My profile clicked');
  };

  const handleNavigation = (tabId: string) => {
    setActiveTab(tabId);
    if (tabId === 'home') {
      window.location.href = '/manager';
    } else if (tabId === 'merchants') {
      window.location.href = '/manager/merchants';
    } else if (tabId === 'reports') {
      window.location.href = '/manager/reports';
    }
    // For other tabs, you can add navigation logic here
  };

  const handleSearch = () => {
    console.log('Search with filters:', filters);
    setCurrentPage(1); // Reset to first page when searching
    fetchEscrowData(); // Fetch data with current filters
  };

  const handleReset = () => {
    setFilters({
      fromDate: '',
      toDate: '',
      paymentType: '',
      transactionId: ''
    });
    setCurrentPage(1); // Reset to first page when resetting filters
    console.log('Filters reset');
    // Fetch data with cleared filters
    setTimeout(() => {
      fetchEscrowData();
    }, 100); // Small delay to ensure state is updated
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    console.log('Page changed to:', page);
    fetchEscrowData(); // Fetch data for the new page
  };

  const renderPagination = () => {
    const pages = [];
    const maxVisiblePages = 10;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }
    if (startPage > 1) {
      pages.push(
        <button key={1} type="button" onClick={() => handlePageChange(1)} className={`w-10 h-10 flex items-center justify-center rounded-lg text-sm font-medium cursor-pointer ${currentPage === 1 ? 'bg-blue-600 text-white border-blue-600' : 'border border-slate-500 bg-white/10 text-slate-200'}`}>1</button>
      );
      if (startPage > 2) pages.push(<span key="ellipsis1" className="px-2 text-slate-400">...</span>);
    }
    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <button key={i} type="button" onClick={() => handlePageChange(i)} className={`w-10 h-10 flex items-center justify-center rounded-lg text-sm font-medium cursor-pointer ${currentPage === i ? 'bg-blue-600 text-white border-blue-600' : 'border border-slate-500 bg-white/10 text-slate-200'}`}>{i}</button>
      );
    }
    if (endPage < totalPages) {
      if (endPage < totalPages - 1) pages.push(<span key="ellipsis2" className="px-2 text-slate-400">...</span>);
      pages.push(
        <button key={totalPages} type="button" onClick={() => handlePageChange(totalPages)} className={`w-10 h-10 flex items-center justify-center rounded-lg text-sm font-medium cursor-pointer ${currentPage === totalPages ? 'bg-blue-600 text-white border-blue-600' : 'border border-slate-500 bg-white/10 text-slate-200'}`}>{totalPages}</button>
      );
    }
    return pages;
  };

  if (loading) {
    return (
      <div className="flex flex-col min-h-screen w-full overflow-x-hidden text-slate-200" style={{ backgroundColor: contentColor }}>
        <ManagerHeader
          user={user}
          onRefresh={() => window.location.reload()}
          onCreditMoney={() => {}}
          onMyProfile={() => window.location.href = '/manager/account'}
          onLogout={() => {}}
        />
        <div className="flex flex-1">
          <div className="max-md:hidden w-[280px] flex-shrink-0 flex flex-col border-r border-white/[0.08]" style={{ backgroundColor: sidebarColor }}>
            <ManagerSidebar activeTab="escrow" />
          </div>
          <div className="flex-1 flex flex-col items-center justify-center p-6 md:p-8 backdrop-blur-sm" style={{ backgroundColor: contentColor }}>
            <div className="w-12 h-12 border-4 border-slate-200 border-t-violet-500 rounded-full animate-spin mb-5" />
            <p className="text-slate-200 text-base m-0">Loading Escrow Transactions...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen w-full overflow-x-hidden text-slate-200" style={{ backgroundColor: contentColor }}>
      <ManagerHeader
        user={user}
        onRefresh={() => window.location.reload()}
        onCreditMoney={() => console.log('Credit Money')}
        onMyProfile={() => window.location.href = '/manager/account'}
        onLogout={() => logout()}
      />
      <div className="flex flex-1">
        <div className="max-md:hidden w-[280px] flex-shrink-0 flex flex-col border-r border-white/[0.08]" style={{ backgroundColor: sidebarColor }}><ManagerSidebar activeTab="escrow" /></div>
        <div className="flex-1 flex flex-col text-slate-200 p-6 md:p-8" style={{ backgroundColor: contentColor }}>
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-slate-100 m-0">Escrow Transactions</h2>
            <button type="button" onClick={() => setShowCreateModal(true)} className="flex items-center gap-2 py-3 px-6 rounded-xl bg-gradient-to-r from-blue-500 to-violet-500 text-white text-sm font-semibold border-none cursor-pointer shadow-md hover:-translate-y-0.5 hover:shadow-lg">
              <span className="text-lg">+</span>
              Create Escrow
            </button>
          </div>

          <div className="rounded-2xl p-6 mb-6 border border-white/[0.06] shadow-xl shadow-black/25" style={{ backgroundColor: sectionColor }}>
            <div className="text-base font-semibold text-slate-300 mb-4">Quick Filters:</div>
            <div className="flex flex-wrap gap-4 items-end">
              <div className="flex flex-col gap-1">
                <label className="text-sm font-medium text-slate-300">From Date</label>
                <input type="date" value={filters.fromDate} onChange={(e) => setFilters(prev => ({ ...prev, fromDate: e.target.value }))} className="py-2.5 px-4 border-2 border-white/[0.12] rounded-lg text-slate-200 bg-slate-800/80 text-sm focus:border-violet-500 focus:ring-2 focus:ring-violet-500/20" />
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-sm font-medium text-slate-300">To Date</label>
                <input type="date" value={filters.toDate} onChange={(e) => setFilters(prev => ({ ...prev, toDate: e.target.value }))} className="py-2.5 px-4 border-2 border-white/[0.12] rounded-lg text-slate-200 bg-slate-800/80 text-sm focus:border-violet-500 focus:ring-2 focus:ring-violet-500/20" />
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-sm font-medium text-slate-300">Payment Type</label>
                <DarkSelect
                  value={filters.paymentType}
                  onChange={(v) => setFilters(prev => ({ ...prev, paymentType: v }))}
                  placeholder="Select Type"
                  options={[
                    { value: '', label: 'Select Type' },
                    { value: 'Debit', label: 'Debit' },
                    { value: 'Credit', label: 'Credit' },
                  ]}
                  className="w-48"
                />
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-sm font-medium text-slate-300">Transaction Id</label>
                <input type="text" placeholder="Enter transaction Id" value={filters.transactionId} onChange={(e) => setFilters(prev => ({ ...prev, transactionId: e.target.value }))} className="py-2.5 px-4 border-2 border-white/[0.12] rounded-lg text-slate-200 bg-slate-800/80 text-sm focus:border-violet-500 focus:ring-2 focus:ring-violet-500/20 w-48" />
              </div>
              <div className="flex gap-2">
                <button type="button" onClick={handleSearch} className="py-2.5 px-5 rounded-lg bg-blue-600 text-white font-semibold text-sm cursor-pointer border-none">Search</button>
                <button type="button" onClick={handleReset} className="py-2.5 px-5 rounded-lg bg-slate-600 text-white font-semibold text-sm cursor-pointer border-none">Reset</button>
              </div>
            </div>
          </div>

          <div className="flex justify-between items-center py-4 mb-4 bg-slate-800/80 rounded-xl px-4 border border-white/[0.08]">
            <div className="text-sm text-slate-300 font-medium">Page {currentPage} of {totalPages}</div>
            <div className="flex items-center gap-2">
              <button type="button" onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} className="w-10 h-10 flex items-center justify-center rounded-lg border border-slate-500 bg-white/10 text-slate-200 font-medium cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed">←</button>
              {renderPagination()}
              <button type="button" onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages} className="w-10 h-10 flex items-center justify-center rounded-lg border border-slate-500 bg-white/10 text-slate-200 font-medium cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed">→</button>
            </div>
          </div>

          <div className="rounded-2xl overflow-x-auto shadow-lg border border-white/[0.08]" style={{ backgroundColor: tableColor }}>
            <table className="w-full min-w-[800px] border-collapse text-sm">
              <thead>
                <tr className="bg-gradient-to-br from-slate-50 to-slate-100">
                  <th className="text-slate-300 font-bold py-4 px-4 text-left border-b-2 border-white/[0.08] text-xs uppercase tracking-wider">Merchant</th>
                  <th className="text-slate-300 font-bold py-4 px-4 text-left border-b-2 border-white/[0.08] text-xs uppercase tracking-wider">Created By</th>
                  <th className="text-slate-300 font-bold py-4 px-4 text-left border-b-2 border-white/[0.08] text-xs uppercase tracking-wider">Type</th>
                  <th className="text-slate-300 font-bold py-4 px-4 text-left border-b-2 border-white/[0.08] text-xs uppercase tracking-wider">Transaction Id</th>
                  <th className="text-slate-300 font-bold py-4 px-4 text-left border-b-2 border-white/[0.08] text-xs uppercase tracking-wider">Date Time</th>
                  <th className="text-slate-300 font-bold py-4 px-4 text-left border-b-2 border-white/[0.08] text-xs uppercase tracking-wider">Amount</th>
                  <th className="text-slate-300 font-bold py-4 px-4 text-left border-b-2 border-white/[0.08] text-xs uppercase tracking-wider">Curr Wallet Bal</th>
                  <th className="text-slate-300 font-bold py-4 px-4 text-left border-b-2 border-white/[0.08] text-xs uppercase tracking-wider">Auto-Release</th>
                  <th className="text-slate-300 font-bold py-4 px-4 text-left border-b-2 border-white/[0.08] text-xs uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody>
                {transactions.map((transaction) => (
                  <tr key={transaction.id} className={transaction.isHighlighted ? 'bg-violet-500/15 hover:bg-violet-500/20' : 'hover:bg-white/[0.06]'}>
                    <td className={`py-4 px-4 border-b border-white/[0.08] text-slate-200 align-middle ${transaction.isHighlighted ? 'font-semibold text-violet-300' : ''}`}>{transaction.merchant}</td>
                    <td className="py-4 px-4 border-b border-white/[0.08] text-slate-200 align-middle">{transaction.createdBy}</td>
                    <td className="py-4 px-4 border-b border-slate-100 align-middle">
                      <span className={`inline-block py-1 px-3 rounded-full text-xs font-semibold uppercase ${transaction.type === 'Credit' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>{transaction.type}</span>
                    </td>
                    <td className="py-4 px-4 border-b border-white/[0.08] text-slate-200 align-middle text-xs">{transaction.transactionId}</td>
                    <td className="py-4 px-4 border-b border-white/[0.08] text-slate-200 align-middle text-xs">{transaction.dateTime}</td>
                    <td className="py-4 px-4 border-b border-white/[0.08] text-slate-200 align-middle">{formatCurrency(transaction.amount)}</td>
                    <td className="py-4 px-4 border-b border-white/[0.08] text-slate-200 align-middle">{formatCurrency(transaction.currentWalletBalance)}</td>
                    <td className="py-4 px-4 border-b border-slate-100 align-middle">
                      {transaction.autoReleaseEnabled ? (
                        <div className="flex flex-col gap-0.5">
                          <span className="inline-block py-1 px-2 rounded-lg text-xs font-semibold bg-amber-100 text-amber-800 w-fit">
                            {transaction.autoReleaseType === 'time' ? '⏰ Time' : transaction.autoReleaseType === 'api' ? '🔗 API' : transaction.autoReleaseType === 'customer' ? '👤 Customer' : 'Auto'}
                          </span>
                          {transaction.autoReleaseDate && <small className="text-slate-500 text-xs">{new Date(transaction.autoReleaseDate).toLocaleDateString()}</small>}
                        </div>
                      ) : (
                        <span className="inline-block py-1 px-2 rounded-lg text-xs font-semibold bg-white/10 text-slate-300 w-fit">Manual</span>
                      )}
                    </td>
                    <td className="py-4 px-4 border-b border-slate-100 align-middle">
                      <button type="button" onClick={() => handleActionClick(transaction)} disabled={transaction.type !== 'Debit' || !transaction.isHighlighted} className="py-2 px-4 rounded-lg bg-blue-600 text-white text-xs font-semibold cursor-pointer border-none disabled:opacity-50 disabled:cursor-not-allowed hover:bg-blue-700">
                        Action
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Logout Modal */}
      <LogoutModal
        isOpen={showLogoutModal}
        onClose={() => setShowLogoutModal(false)}
        onConfirm={handleLogout}
        userName={user?.name}
        userRole={user?.role}
      />

      {showCreateModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[10000] p-5 backdrop-blur-sm" onClick={handleCloseCreateModal}>
          <div className="bg-white rounded-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center py-4 px-5 border-b border-slate-200 bg-gradient-to-br from-slate-50 to-slate-100">
              <h3 className="text-lg font-bold text-slate-800 m-0">Create Escrow Transaction</h3>
              <button type="button" className="w-10 h-10 flex items-center justify-center rounded-lg border-none text-slate-500 cursor-pointer hover:bg-slate-200 text-2xl" onClick={handleCloseCreateModal}>×</button>
            </div>
            <div className="p-6">
              <form onSubmit={handleCreateEscrow} className="space-y-4">
                <div className="flex flex-col gap-1">
                  <label className="font-semibold text-slate-800 text-sm">Merchant *</label>
                  <div className="relative">
                    <input type="text" placeholder="Search merchants (e.g., John Smith)" value={merchantSearchTerm} onChange={handleMerchantSearchChange} onFocus={() => setShowMerchantDropdown(true)} onBlur={() => setTimeout(() => setShowMerchantDropdown(false), 200)} className="w-full py-2.5 px-4 border-2 border-white/[0.12] rounded-lg text-slate-200 bg-slate-800/80 text-sm focus:border-violet-500 focus:ring-2 focus:ring-violet-500/20" required />
                    {showMerchantDropdown && (
                      <div className="absolute top-full left-0 right-0 mt-1 bg-white border-2 border-slate-200 rounded-lg shadow-lg z-10 max-h-48 overflow-y-auto">
                        {merchantSearchLoading ? <div className="py-3 px-4 text-slate-500 text-sm">Searching merchants...</div> : filteredMerchants.length > 0 ? filteredMerchants.map((merchant) => (<div key={merchant.id} className="py-2.5 px-4 hover:bg-slate-100 cursor-pointer text-slate-800 text-sm" onClick={() => handleMerchantSelect(merchant)}>{merchant.name}</div>)) : merchantSearchTerm.trim().length >= 2 ? <div className="py-3 px-4 text-slate-500 text-sm">No merchants found matching "{merchantSearchTerm}"</div> : <div className="py-3 px-4 text-slate-500 text-sm">Type at least 2 characters to search merchants</div>}
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex flex-col gap-1">
                  <label className="font-semibold text-slate-800 text-sm">Transaction Type *</label>
                  <DarkSelect
                    value={createFormData.transactionType}
                    onChange={(v) => setCreateFormData(prev => ({ ...prev, transactionType: v }))}
                    options={[
                      { value: 'debit', label: "Escrow Hold (Debit): Holds funds from merchant's wallet" },
                      { value: 'credit', label: "Escrow Credit (Release): Adds funds to merchant's wallet" },
                    ]}
                    className="w-full"
                  />
                </div>
                <div className="flex flex-col gap-1">
                  <label className="font-semibold text-slate-800 text-sm">Amount (₹) *</label>
                  <input type="number" step="0.01" value={createFormData.amount} onChange={(e) => setCreateFormData(prev => ({ ...prev, amount: e.target.value }))} className="w-full py-2.5 px-4 border-2 border-white/[0.12] rounded-lg text-slate-200 bg-slate-800/80 text-sm focus:border-violet-500 focus:ring-2 focus:ring-violet-500/20" required />
                </div>
                <div className="flex flex-col gap-1">
                  <label className="font-semibold text-slate-800 text-sm">Description</label>
                  <input type="text" value={createFormData.description} onChange={(e) => setCreateFormData(prev => ({ ...prev, description: e.target.value }))} className="w-full py-2.5 px-4 border-2 border-white/[0.12] rounded-lg text-slate-200 bg-slate-800/80 text-sm focus:border-violet-500 focus:ring-2 focus:ring-violet-500/20" />
                </div>
                <div className="flex flex-col gap-1">
                  <label className="font-semibold text-slate-800 text-sm">Contact Info</label>
                  <input type="text" value={createFormData.contactInfo} onChange={(e) => setCreateFormData(prev => ({ ...prev, contactInfo: e.target.value }))} className="w-full py-2.5 px-4 border-2 border-white/[0.12] rounded-lg text-slate-200 bg-slate-800/80 text-sm focus:border-violet-500 focus:ring-2 focus:ring-violet-500/20" />
                </div>
                <div className="flex flex-col gap-1">
                  <label className="font-semibold text-slate-800 text-sm">Payment Method</label>
                  <input type="text" value={createFormData.paymentMethod} onChange={(e) => setCreateFormData(prev => ({ ...prev, paymentMethod: e.target.value }))} className="w-full py-2.5 px-4 border-2 border-white/[0.12] rounded-lg text-slate-200 bg-slate-800/80 text-sm focus:border-violet-500 focus:ring-2 focus:ring-violet-500/20" />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex flex-col gap-1">
                    <label className="font-semibold text-slate-800 text-sm">Commission Amount (₹)</label>
                    <input type="number" step="0.01" value={createFormData.commissionAmount} onChange={(e) => setCreateFormData(prev => ({ ...prev, commissionAmount: e.target.value }))} className="w-full py-2.5 px-4 border-2 border-white/[0.12] rounded-lg text-slate-200 bg-slate-800/80 text-sm focus:border-violet-500 focus:ring-2 focus:ring-violet-500/20" />
                  </div>
                  <div className="flex flex-col gap-1">
                    <label className="font-semibold text-slate-800 text-sm">Processing Fee (₹)</label>
                    <input type="number" step="0.01" value={createFormData.processingFee} onChange={(e) => setCreateFormData(prev => ({ ...prev, processingFee: e.target.value }))} className="w-full py-2.5 px-4 border-2 border-white/[0.12] rounded-lg text-slate-200 bg-slate-800/80 text-sm focus:border-violet-500 focus:ring-2 focus:ring-violet-500/20" />
                  </div>
                </div>
                <div className="border-t border-slate-200 pt-4 mt-4">
                  <h4 className="text-base font-bold text-slate-800 mb-3 m-0">Auto-Release Configuration</h4>
                  <label className="flex items-center gap-2 cursor-pointer mb-3">
                    <input type="checkbox" checked={createFormData.autoReleaseEnabled} onChange={(e) => setCreateFormData(prev => ({ ...prev, autoReleaseEnabled: e.target.checked, autoReleaseType: e.target.checked ? prev.autoReleaseType : 'manual' }))} className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500" />
                    <span className="text-slate-800 text-sm font-medium">Enable Automatic Release</span>
                  </label>
                  {createFormData.autoReleaseEnabled && (
                    <>
                      <div className="flex flex-col gap-1 mb-3">
                        <label className="font-semibold text-slate-800 text-sm">Release Method</label>
                        <DarkSelect
                          value={createFormData.autoReleaseType}
                          onChange={(v) => setCreateFormData(prev => ({ ...prev, autoReleaseType: v, autoReleaseValue: '' }))}
                          options={[
                            { value: 'time', label: 'Time-based (After X days)' },
                            { value: 'api', label: 'API Callback' },
                            { value: 'customer', label: 'Customer Confirmation' },
                          ]}
                          className="w-full"
                        />
                      </div>
                      <div className="flex flex-col gap-1">
                        {createFormData.autoReleaseType === 'time' && (<><label className="font-semibold text-slate-800 text-sm">Release After (Days)</label><input type="number" min={1} max={365} value={createFormData.autoReleaseValue} onChange={(e) => setCreateFormData(prev => ({ ...prev, autoReleaseValue: e.target.value }))} placeholder="e.g., 7 for 7 days" className="w-full py-2.5 px-4 border-2 border-white/[0.12] rounded-lg text-slate-200 bg-slate-800/80 text-sm focus:border-violet-500 focus:ring-2 focus:ring-violet-500/20" /></>)}
                        {createFormData.autoReleaseType === 'api' && (<><label className="font-semibold text-slate-800 text-sm">Callback URL</label><input type="url" value={createFormData.autoReleaseValue} onChange={(e) => setCreateFormData(prev => ({ ...prev, autoReleaseValue: e.target.value }))} placeholder="https://your-domain.com/webhook/escrow-release" className="w-full py-2.5 px-4 border-2 border-white/[0.12] rounded-lg text-slate-200 bg-slate-800/80 text-sm focus:border-violet-500 focus:ring-2 focus:ring-violet-500/20" /><small className="text-slate-500 text-xs mt-1">We'll send a POST request to this URL. Respond with {"{success: true}"} to release funds.</small></>)}
                        {createFormData.autoReleaseType === 'customer' && (<><label className="font-semibold text-slate-800 text-sm">Customer Email</label><input type="email" value={createFormData.autoReleaseValue} onChange={(e) => setCreateFormData(prev => ({ ...prev, autoReleaseValue: e.target.value }))} placeholder="customer@example.com" className="w-full py-2.5 px-4 border-2 border-white/[0.12] rounded-lg text-slate-200 bg-slate-800/80 text-sm focus:border-violet-500 focus:ring-2 focus:ring-violet-500/20" /><small className="text-slate-500 text-xs mt-1">Customer will receive an email with a link to approve fund release.</small></>)}
                      </div>
                    </>
                  )}
                </div>
                <div className="flex justify-end gap-2 pt-4 border-t border-slate-200 mt-4">
                  <button type="button" className="py-2.5 px-5 rounded-lg font-semibold text-sm cursor-pointer bg-white text-slate-600 border-2 border-slate-200 hover:bg-slate-50" onClick={handleCloseCreateModal}>Cancel</button>
                  <button type="submit" className="py-2.5 px-5 rounded-lg font-semibold text-sm cursor-pointer bg-blue-600 text-white border-none hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed" disabled={submitting}>{submitting ? 'Creating...' : 'Create Escrow'}</button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {showActionModal && selectedTransaction && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[10000] p-5 backdrop-blur-sm" onClick={handleCloseActionModal}>
          <div className="bg-white rounded-xl max-w-lg w-full shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center py-4 px-5 border-b border-slate-200 bg-gradient-to-br from-slate-50 to-slate-100">
              <h3 className="text-lg font-bold text-slate-800 m-0">Escrow Action</h3>
              <button type="button" className="w-10 h-10 flex items-center justify-center rounded-lg border-none text-slate-500 cursor-pointer hover:bg-slate-200 text-2xl" onClick={handleCloseActionModal}>×</button>
            </div>
            <div className="p-6">
              <div className="mb-4 p-4 bg-slate-50 rounded-lg text-slate-700 text-sm">
                <p className="m-0 mb-1"><strong>Merchant:</strong> {selectedTransaction.merchant}</p>
                <p className="m-0 mb-1"><strong>Amount:</strong> {formatCurrency(selectedTransaction.amount)}</p>
                <p className="m-0"><strong>Transaction ID:</strong> {selectedTransaction.transactionId}</p>
              </div>
              <form onSubmit={handleEscrowAction} className="space-y-4">
                <div className="flex flex-col gap-1">
                  <label className="font-semibold text-slate-800 text-sm">Action *</label>
                  <DarkSelect
                    value={actionFormData.action}
                    onChange={(v) => setActionFormData(prev => ({ ...prev, action: v }))}
                    options={[
                      { value: 'release', label: 'Release Funds' },
                      { value: 'refund', label: 'Refund Funds' },
                    ]}
                    className="w-full"
                  />
                </div>
                <div className="flex flex-col gap-1">
                  <label className="font-semibold text-slate-800 text-sm">Processed By *</label>
                  <AutocompleteInput value={actionFormData.processedBy} onChange={(value) => setActionFormData(prev => ({ ...prev, processedBy: value }))} onSelect={handleAutocompleteSelect} placeholder="Type name, email, or merchant ID" required options={autocompleteOptions} loading={loadingAutocomplete} />
                </div>
                <div className="flex justify-end gap-2 pt-4 border-t border-slate-200">
                  <button type="button" className="py-2.5 px-5 rounded-lg font-semibold text-sm cursor-pointer bg-white text-slate-600 border-2 border-slate-200 hover:bg-slate-50" onClick={handleCloseActionModal}>Cancel</button>
                  <button type="submit" className="py-2.5 px-5 rounded-lg font-semibold text-sm cursor-pointer bg-blue-600 text-white border-none hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed" disabled={submitting}>{submitting ? 'Processing...' : `${actionFormData.action === 'release' ? 'Release' : 'Refund'} Funds`}</button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default ManagerEscrow;
